// src/utils/procurementNeed.js
// D-304 B (Stabilization P2) — what the Procurement Queue must buy, worked
// out LIVE every time the queue opens. Pure functions, no Firebase — tested
// by src/utils/procurementNeed.test.js.
//
// WHY THIS FILE EXISTS
// The old queue decided what to show from flags that were written once and
// never cleared (purchase_status "batched", item_status
// "approved_pending_issue"). A PR never converted, a PO cancelled or short-
// closed, or stock used elsewhere → the line stayed hidden forever (seen:
// a copper pipe line hidden since 14 Sep). Now, per item:
//
//     To buy = Need − In store − Already ordered
//
//   Need            Σ (approved − issued) of every open MRF line of that item
//   In store        live central stock (inventory_summary.qty)
//   Already ordered open PRs not yet converted + open store POs not received
//
// Nothing has to be "un-set": cancel a PR, short-close a PO, issue stock
// elsewhere — the next queue load shows the right number.
//
// IDENTITY RULE (CORE_RULES §81): items are matched by item_id ONLY (or a
// legacy line's Firestore doc id). Never by code or name — names and codes
// repeat (13 copper pipes share one code; every client uses similar names).

import { isRealStockDoc, pickStockDoc } from "./stockDocs";

// Categories that are bought into store stock. Legacy names kept (they
// exist on older items) until Item Master categories are cleaned up.
export const STOCK_CATEGORIES = new Set([
  "material", "consumable",
  "general", "copper", "electrical", "refrigerant", "ducting",
  "hardware", "consumables", "spares", "safety", "testing",
  "vrf/vrv", "chiller", "split ac",
]);

// Tracked as assets (Asset Registry), never bought into stock here.
export const TOOL_CATEGORIES = new Set(["tools", "tool", "asset", "assets"]);

// MRF line states that need nothing more from purchasing.
const CLOSED_LINE_STATES = new Set(["issued", "cancelled", "redirected_to_asset_registry"]);

// PO states that still mean "material is coming".
const OPEN_PO_STATES = new Set(["draft", "approved", "ordered"]);

export const ATTENTION_REASONS = {
  custom_item:        "Not in Item Master — open the MRF to add it to Item Master (material) or the Asset Registry (tool)",
  not_in_item_master: "Item not found in Item Master",
  inactive_item:      "Item is inactive in Item Master",
  tool_or_asset:      "Tool / asset — goes to the Asset Registry, not stock",
  category_missing:   "Category missing — set it in Item Master",
  not_stock_category: "Category is not a stock material — check Item Master",
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
export const roundQty = (n) => Math.round(num(n) * 1e6) / 1e6;
const clean = (v) => String(v ?? "").trim();
const millisOf = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

// Indian financial year (April–March), e.g. 18 Oct 2026 → "26-27",
// 15 Feb 2027 → "26-27", 1 Apr 2027 → "27-28".
export const financialYearOf = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const startYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  const yy = (y) => String(y % 100).padStart(2, "0");
  return `${yy(startYear)}-${yy(startYear + 1)}`;
};

// Item Master lookup — by canonical item_id, plus legacy Firestore doc id.
export const buildMasterIndex = (items = []) => {
  const byItemId = new Map();
  const byDocId  = new Map();
  for (const it of items) {
    if (!it) continue;
    const id = clean(it.item_id);
    if (id && !byItemId.has(id)) byItemId.set(id, it);
    if (it.id) byDocId.set(clean(it.id), it);
  }
  return { byItemId, byDocId };
};

export const findMaster = (index, id) => {
  const key = clean(id);
  if (!key || !index) return null;
  return index.byItemId.get(key) || index.byDocId.get(key) || null;
};

// Central stock per item_id. Only real stock docs count (stockDocs.js, the one
// rule): old junk docs from removed Cloud Functions carry current_stock instead
// (MED-22). Doc id = item_id, else the ONE real doc; 2+ legacy docs with none on
// the item's id = no figure, never a guess.
// inventoryDocs: [{ id, data }]
export const stockByItem = (inventoryDocs = []) => {
  const docsByItem = new Map();
  for (const d of inventoryDocs) {
    if (!isRealStockDoc(d?.data)) continue;
    const itemId = clean(d.data.item_id);
    if (!itemId) continue;
    if (!docsByItem.has(itemId)) docsByItem.set(itemId, []);
    docsByItem.get(itemId).push(d);
  }
  const out = new Map();
  for (const [itemId, docs] of docsByItem) {
    const picked = pickStockDoc(docs, itemId);
    if (picked) out.set(itemId, picked.data.qty);
  }
  return out;
};

// Quantity still coming in, per item_id, with the PR / PO numbers behind it.
// purchaseOrders / purchaseRequests: [{ id, data }]
export const openOrdersByItem = ({ purchaseOrders = [], purchaseRequests = [] } = {}) => {
  const out = new Map();
  const add = (itemId, qty, ref) => {
    if (!itemId || !(qty > 0)) return;
    const cur = out.get(itemId) || { qty: 0, refs: [] };
    cur.qty = roundQty(cur.qty + qty);
    const existing = cur.refs.find((r) => r.type === ref.type && r.id === ref.id);
    if (existing) existing.qty = roundQty(existing.qty + qty);
    else cur.refs.push({ ...ref, qty });
    out.set(itemId, cur);
  };

  for (const po of purchaseOrders) {
    const d = po?.data || {};
    if (d.grn_locked) continue;
    if (!OPEN_PO_STATES.has(clean(d.po_status))) continue;
    if (d.purchase_mode === "direct_site_purchase") continue; // delivered to a site, not the store
    for (const line of d.items || []) {
      if (!line || line._cancelled || line.is_custom_item) continue;
      const itemId = clean(line.item_id);
      if (!itemId || itemId.startsWith("custom_")) continue;
      const open = num(line.ordered_qty ?? line.qty) - num(line.received_qty);
      add(itemId, open, { type: "PO", id: po.id, no: d.po_number || po.id });
    }
  }

  for (const pr of purchaseRequests) {
    const d = pr?.data || {};
    const st = clean(d.status);
    const pst = clean(d.procurement_status);
    if (st === "cancelled" || pst === "cancelled") continue;
    if (d.po_id || d.po_created || st === "po_created" || pst === "po_generated") continue;
    for (const line of d.items || []) {
      if (!line || line.deleted) continue;
      const itemId = clean(line.item_id);
      if (!itemId || line.is_custom_item) continue;
      // New PRs (D-304 B) store order_qty. Older PR rows stored the MRF
      // line's approved qty in `qty` and the real gap in remaining_qty.
      const q = line.order_qty !== undefined
        ? num(line.order_qty)
        : num(line.remaining_qty ?? line.qty);
      add(itemId, q, { type: "PR", id: pr.id, no: d.purchase_request_no || pr.id });
    }
  }
  return out;
};

// Every open MRF line that still needs material, plus the lines that need a
// person to fix something first (never silently skipped).
// mrfDocs: [{ id, data }]
export const collectNeedLines = (mrfDocs = [], masterIndex) => {
  const lines = [];
  const attention = [];

  for (const mrf of mrfDocs) {
    const d = mrf?.data || {};
    const status = clean(d.status);
    if (status !== "approved" && status !== "partially_issued") continue;
    if (clean(d.request_type) === "tool") continue; // tool requests are asset transfers

    (d.items || []).forEach((item, itemIndex) => {
      if (!item) return;
      if (CLOSED_LINE_STATES.has(clean(item.item_status))) return;

      const procurementType = clean(item.procurement_type || item.procurement || "purchase").toLowerCase();
      if (procurementType !== "purchase") return;

      const approved = num(item.approved_qty ?? item.qty);
      const issued   = num(item.issued_qty);
      const need     = roundQty(Math.max(0, approved - issued));
      if (need <= 0) return;

      const base = {
        mrf_id:       mrf.id,
        mrf_no:       d.mrf_no || "",
        item_index:   itemIndex,
        site_id:      d.site_id || "",
        site_name:    d.site_name || "",
        project_name: d.project_name || "",
        item_name:    item.item_name || "",
        unit:         item.unit || item.uom || "",
        approved,
        issued,
        need,
        created_ms:   millisOf(d.approved_at) || millisOf(d.created_at),
      };

      const itemId = clean(item.item_id);
      if (item.is_custom_item || !itemId || itemId.startsWith("custom_")) {
        attention.push({ ...base, item_id: itemId, reason: "custom_item" });
        return;
      }

      const master = findMaster(masterIndex, itemId);
      if (!master) {
        attention.push({ ...base, item_id: itemId, reason: "not_in_item_master" });
        return;
      }
      if (master.is_active === false) {
        attention.push({ ...base, item_id: clean(master.item_id) || itemId, reason: "inactive_item" });
        return;
      }

      const category = clean(master.category || item.category).toLowerCase();
      const canonicalId = clean(master.item_id) || itemId;
      if (item.is_tool || TOOL_CATEGORIES.has(category)) {
        attention.push({ ...base, item_id: canonicalId, reason: "tool_or_asset", category });
        return;
      }
      if (!category) {
        attention.push({ ...base, item_id: canonicalId, reason: "category_missing" });
        return;
      }
      if (!STOCK_CATEGORIES.has(category)) {
        attention.push({ ...base, item_id: canonicalId, reason: "not_stock_category", category });
        return;
      }

      lines.push({ ...base, item_id: canonicalId, category, master });
    });
  }
  return { lines, attention };
};

// One row per item: Need / In store / Ordered / To buy.
// status: "to_buy" | "ordered" (covered by open PR/PO) | "in_store" (stock covers it)
export const buildProcurementRows = ({ needLines = [], stock = new Map(), openOrders = new Map() } = {}) => {
  const byItem = new Map();
  for (const line of needLines) {
    const cur = byItem.get(line.item_id);
    if (cur) cur.lines.push(line);
    else byItem.set(line.item_id, { item_id: line.item_id, master: line.master, lines: [line] });
  }

  const rows = [];
  for (const { item_id, master, lines } of byItem.values()) {
    lines.sort((a, b) => a.created_ms - b.created_ms || String(a.mrf_id).localeCompare(String(b.mrf_id)) || a.item_index - b.item_index);
    const need     = roundQty(lines.reduce((s, l) => s + l.need, 0));
    const inStore  = Math.max(0, num(stock.get(item_id)));
    const orders   = openOrders.get(item_id) || { qty: 0, refs: [] };
    const toBuy    = roundQty(Math.max(0, need - inStore - orders.qty));
    const status   = toBuy > 0 ? "to_buy" : (inStore >= need ? "in_store" : "ordered");

    rows.push({
      item_id,
      item_code:     master?.item_code || "",
      item_name:     master?.item_name || lines[0].item_name || "",
      specification: master?.specification || "",
      unit:          master?.unit || lines[0].unit || "nos",
      category:      lines[0].category,
      preferred_vendor_id:   master?.preferred_vendor_id || "",
      preferred_vendor_name: master?.preferred_vendor_name || "",
      need,
      in_store:      inStore,
      ordered:       orders.qty,
      ordered_refs:  orders.refs,
      to_buy:        toBuy,
      status,
      lines,
      sites:         [...new Set(lines.map((l) => l.site_name).filter(Boolean))],
      mrf_count:     new Set(lines.map((l) => l.mrf_id)).size,
    });
  }
  rows.sort((a, b) => {
    const rank = { to_buy: 0, ordered: 1, in_store: 2 };
    return rank[a.status] - rank[b.status] || a.item_name.localeCompare(b.item_name);
  });
  return rows;
};

// The part of each MRF line's need NOT already covered by store stock or
// open orders. Cover is applied to the OLDEST lines first (they were
// approved first), so a new order is linked to the lines still uncovered.
export const uncoveredLines = (row) => {
  let cover = roundQty(num(row?.in_store) + num(row?.ordered));
  const out = [];
  for (const l of row?.lines || []) {
    const take = Math.min(cover, l.need);
    cover = roundQty(cover - take);
    const left = roundQty(l.need - take);
    if (left > 0) out.push({ ...l, need: left });
  }
  return out;
};

// Which MRF lines a PR quantity is for — oldest first, up to each line's
// need. Quantity beyond the total need (e.g. a full box) is plain stock
// top-up and is not tied to any line.
export const allocateSources = (lines = [], qty = 0) => {
  let left = roundQty(qty);
  const out = [];
  for (const l of lines) {
    if (left <= 0) break;
    const take = roundQty(Math.min(left, l.need));
    if (take <= 0) continue;
    out.push({
      mrf_id:     l.mrf_id,
      mrf_no:     l.mrf_no || "",
      item_index: l.item_index,
      site_id:    l.site_id || "",
      site_name:  l.site_name || "",
      qty:        take,
    });
    left = roundQty(left - take);
  }
  return out;
};